import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { BosfedLogoName } from './RegisteredName';

const navLinks = [
  { label: 'Home', to: '/', end: true },
  { label: 'About', to: '/about' },
  { label: 'Capabilities', to: '/capabilities' },
  { label: 'Payroll & Compliance', to: '/payroll-compliance' },
  { label: 'Industries', to: '/industries' },
  { label: 'Innovation', to: '/innovation' },
  { label: 'Capital Partners', to: '/capital-partners' },
  { label: 'Client Portal', to: '/client-portal' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/90'
          : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-5 sm:px-8 lg:px-10 2xl:px-16">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <NavLink to="/" end data-testid="nav-logo" className="inline-flex leading-none group">
            <span className="logo-text text-[#0B1F33] text-[27px] md:text-[31px] group-hover:text-[#1FA8F5] transition-colors duration-200">
              <BosfedLogoName />
            </span>
          </NavLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-5">
            {navLinks.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, '').replace(/--/g, '-')}`}
                className={({ isActive }) =>
                  `text-[12px] xl:text-[13px] font-medium transition-colors duration-200 relative ${
                    isActive
                      ? 'text-[#1FA8F5]'
                      : 'text-[#40516A] hover:text-[#1FA8F5]'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <button
              data-testid="nav-contact-cta"
              onClick={() => navigate('/contact')}
              className="bg-[#061E35] text-white px-4 xl:px-5 py-2 rounded-md text-sm font-semibold hover:bg-[#0B8ED8] transition-colors duration-300 inline-flex items-center gap-1.5"
            >
              Contact Us <ArrowRight size={14} />
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="nav-mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden p-2 text-[#0B1F33] hover:text-[#1FA8F5] transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white px-4 py-4">
          <div className="space-y-1">
            {navLinks.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                onClick={() => setMenuOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, '').replace(/--/g, '-')}`}
                className={({ isActive }) =>
                  `block py-2.5 px-3 rounded-md text-sm font-medium transition-colors ${
                    isActive ? 'text-[#1FA8F5] bg-[#1FA8F5]/5' : 'text-[#0B1F33] hover:bg-slate-50'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-4 pt-4">
            <button
              data-testid="mobile-contact-cta"
              onClick={() => { navigate('/contact'); setMenuOpen(false); }}
              className="w-full bg-[#061E35] text-white py-2.5 rounded-md text-sm font-semibold hover:bg-[#0B8ED8] transition-colors inline-flex items-center justify-center gap-1.5"
            >
              Contact Us <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
